import React, { useState, useLayoutEffect } from 'react';
import moment from 'moment';
import { AbortError, setDocumentTitle, api } from 'js/utils';
import Crumb from 'js/components/breadcrumbs/crumb';
import Layout from 'js/components/layout/layout';
import Navigation from 'js/components/navigation/navigation';
import TopBar from 'js/components/top-bar/top-bar';
import StatusList from './status-list';

const auditStartDate = new Date(Date.now() - 8 * 24 * 60 * 60 * 1000);
const auditParams = {
    activity_date_after: auditStartDate.toISOString().slice(0, 10),
};

const endpoints = [
    {
        id: 'eventEndpoint',
        urls: {
            development: 'http://localhost:8082/health/',
            production: 'https://ptingless.4d.silverbulletcloud.com/health/',
            staging:
                'https://ptingless-{CLUSTER}.4d.silverbulletcloud.com/health/',
        },
    },
    {
        id: 'iristvEndpoint',
        urls: {
            development: 'http://localhost:8086/health/',
            production: 'https://iristv.4d.silverbulletcloud.com/health/',
            staging:
                'https://iristv-{CLUSTER}.4d.silverbulletcloud.com/health/',
        },
    },
    {
        id: 'serveEndpoint',
        urls: {
            development: 'http://localhost:8083/health/',
            production: 'https://serve.4d.silverbulletcloud.com/health/',
            staging: 'https://serve-{CLUSTER}.4d.silverbulletcloud.com/health/',
        },
    },
    {
        id: 'xandrEndpoint',
        urls: {
            development: 'http://localhost:8085/ready',
            production: 'http://xandr.4d.silverbulletcloud.com/ready',
            staging: 'http://xandr-{CLUSTER}.4d.silverbulletcloud.com/ready',
        },
    },
    {
        id: 'treasuredataEndpoint',
        urls: {
            development: 'http://localhost:8084/health/',
            production: 'https://treasuredata.4d.silverbulletcloud.com/health/',
            staging:
                'https://treasuredata-{CLUSTER}.4d.silverbulletcloud.com/health/',
        },
    },
    // Pubmatic endpoint has a whitelist, uncomment if we want to allow all traffic
    // {
    //     id: 'pubmaticEndpoint',
    //     urls: {
    //         development: 'http://localhost:8087/health/',
    //         production: 'https://pubmatic.4d.silverbulletcloud.com/health/',
    //         staging:
    //             'https://pubmatic-{CLUSTER}.4d.silverbulletcloud.com/health/',
    //     },
    // },
    {
        id: 'ttdEndpoint',
        urls: {
            development: 'http://localhost:8088/health/',
            production:
                'https://us-east-1-ttd.4d.silverbulletcloud.com/health/',
            staging:
                'https://us-east-1-ttd-{CLUSTER}.4d.silverbulletcloud.com/health/',
        },
    },
    {
        id: 'liveconnectEndpoint',
        urls: {
            development: 'http://localhost:8090/health/',
            production: 'https://enrichment.4d.silverbulletcloud.com/health/',
            staging:
                'https://enrichment-{CLUSTER}.4d.silverbulletcloud.com/health/',
        },
    },
];

function StatusPage() {
    const [integrationStatuses, setIntegrationStatuses] = useState([]);
    const [endpointStatuses, setEndpointStatuses] = useState([]);
    const [internalStatuses, setInternalStatuses] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [hasError, setError] = useState(false);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(false);

            const [
                // Integration Data
                { results: adformData },
                { results: crimtanData },
                { results: iristvData },
                { results: treasuredataData },
                { results: xandrData },
                { results: pubmaticData },
                { results: ttdData },
                { results: openxData },
                { results: liveconnectData },
                // Internal Data
                { results: pageData },
                { results: videoData },
                { results: reportData },
            ] = await Promise.all([
                // Integration Endpoints
                api().auditAdform.list(auditParams),
                api().auditCrimtan.list(auditParams),
                api().auditIristv.list(auditParams),
                api().auditTreasuredata.list(auditParams),
                api().auditXandr.list(auditParams),
                api().auditPubmatic.list(auditParams),
                api().auditTtd.list(auditParams),
                api().auditOpenX.list(auditParams),
                api().auditLiveconnect.list(auditParams),
                // Internal Endpoints
                api().auditPage.list(auditParams),
                api().auditVideo.list(auditParams),
                api().auditReport.list({ ...auditParams, complete: true }),
            ]);

            const isRecent7Days = (item) =>
                moment(item.activity_date) > moment().subtract(7, 'days');
            setIntegrationStatuses([
                { id: 'xandr', data: xandrData.filter(isRecent7Days) },
                { id: 'iristv', data: iristvData.filter(isRecent7Days) },
                { id: 'adform', data: adformData.filter(isRecent7Days) },
                { id: 'crimtan', data: crimtanData.filter(isRecent7Days) },
                {
                    id: 'treasuredata',
                    data: treasuredataData.filter(isRecent7Days),
                },
                { id: 'pubmatic', data: pubmaticData.filter(isRecent7Days) },
                { id: 'ttd', data: ttdData.filter(isRecent7Days) },
                { id: 'openx', data: openxData.filter(isRecent7Days) },
                {
                    id: 'liveconnect',
                    data: liveconnectData.filter(isRecent7Days),
                },
            ]);

            const statuses = [];

            await endpoints.reduce(async (promise, endpoint) => {
                // This line will wait for the last async function to finish.
                // The first iteration uses an already resolved Promise
                // so, it will immediately continue.
                await promise;
                try {
                    let url;

                    if (process.env.REACT_APP_CLUSTER === 'dev') {
                        url = endpoint.urls.development;
                    } else if (process.env.REACT_APP_CLUSTER === 'avocado') {
                        url = endpoint.urls.production;
                    } else {
                        url = endpoint.urls.staging.replace(
                            '{CLUSTER}',
                            process.env.REACT_APP_CLUSTER,
                        );
                    }

                    const response = await fetch(url);

                    statuses.push({
                        id: endpoint.id,
                        data: [{ is_anomaly: response.status !== 200 }],
                    });
                } catch (e) {
                    statuses.push({
                        id: endpoint.id,
                        data: [{ is_anomaly: true }],
                    });
                }
            }, Promise.resolve());

            setEndpointStatuses(statuses);

            setInternalStatuses([
                { id: 'displayContexts', data: pageData.filter(isRecent7Days) },
                { id: 'videoContexts', data: videoData.filter(isRecent7Days) },
                { id: 'insights', data: reportData.filter(isRecent7Days) },
            ]);
        } catch (errors) {
            if (errors instanceof AbortError) return;

            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useLayoutEffect(() => {
        setDocumentTitle(['Status Dashboard', 'Settings']);
        loadData();

        return () => {
            api().abortAll();
        };
    }, []);

    return (
        <Layout
            sidebar={<Navigation />}
            header={
                <TopBar
                    breadcrumbs={[
                        <Crumb key="home" to="/" label="4D" />,
                        <Crumb
                            key="settings"
                            to="/settings/"
                            label="Settings"
                        />,
                        <Crumb
                            key="status-dashboard"
                            label="Status Dashboard"
                        />,
                    ]}
                />
            }
        >
            <StatusList
                integrationStatuses={integrationStatuses}
                endpointStatuses={endpointStatuses}
                internalStatuses={internalStatuses}
                isLoading={isLoading}
                hasError={hasError}
                onRetry={loadData}
            />
        </Layout>
    );
}

export default StatusPage;
